import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Form, Input, Result, Skeleton, Space } from 'antd';
import dayjs from 'dayjs';
import { IKeys } from 'models';
import { IFollowups } from 'models/followups.model';
import { workService } from 'services';
import { userStore } from 'stores';
import { followUpsMessages } from 'utils';

interface FollowUpsProps {
	workId: number;
	customer_phone: string;
}

const { Item, useForm } = Form;

export const FollowUps = ({ customer_phone, workId }: FollowUpsProps) => {
	const [form] = useForm();
	const client = useQueryClient();
	const { data, isLoading } = useQuery({
		queryFn: () => workService.getFollowups(customer_phone),
		queryKey: [IKeys.FOLLOWUPS, customer_phone]
	});

	const { mutate, isPending } = useMutation({
		mutationFn: (data: Partial<IFollowups>) => workService.createFollowup(data),
		onSuccess: (response) => {
			client.setQueryData([IKeys.FOLLOWUPS, customer_phone], (stored?: IFollowups[]) => {
				const newArr = [response, ...(stored || [])];
				form.setFieldValue(['acf', 'message'], followUpsMessages[newArr.length]);
				return newArr;
			});
		}
	});

	return isLoading ? <Skeleton /> : (
		<Space vertical style={{ width: '100%' }}>
			<Form 
				form={form}
				onFinish={(values) => {
					values.acf.work_id = +workId;
					values.acf.customer_phone = customer_phone;
					values.author = userStore.data?.id;
					values.status = 'publish';
					mutate(values);
				}}
				layout="vertical"
				size="large"
				initialValues={{
					acf: {
						message: (data?.length && data?.length > 4) ? '' : followUpsMessages[data?.length || 0]
					}
				}}
			>
				<Item label="Message" name={['acf', 'message']}>
					<Input.TextArea autoSize style={{ width: '100%' }}/>
				</Item>
				<Item>
					<Button
						loading={isPending}
						type="primary"
						htmlType="submit"
						size="large"
					>
						Send
					</Button>
				</Item>
			</Form>
			{(data?.length && data?.length > 0) ? data?.map((message) => {
				const date = dayjs(message.date).format('MM/DD/YYYY HH:mm').toString();
				const author = userStore.getUser(Number(message.author));
				const isCustomer = author?.id === 1;
				return (
					<Alert
						key={message.id}
						title={`${date} by ${isCustomer ? 'Customer' : author?.name}`}
						description={message.acf.message}
						type={isCustomer ? 'success' : 'info'}
					/>
				);
			}) : (
				  <Result
					status="404"
					title="No follow ups"
					subTitle="You haven't sent any messages."
				/>
			)}
		</Space>
	);
};